import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { useNavigate } from "react-router"
import {
  ArrowRight,
  CalendarCheck01,
  Check,
  MarkerPin01,
  MessageChatCircle,
} from "@untitledui/icons"
import { toast } from "sonner"
import { ApiError } from "@/lib/api/client"
import {
  publicConsumerApi,
  type DecodedVehicle,
  type IntakeDetailsPatch,
  type IntakeVehiclePatch,
  type PublicShopProfile,
} from "@/lib/api/public-consumer"
import { publicOrigin, withShopName } from "@/lib/intake/intake-config"
import { sideLabel } from "@/lib/estimates/catalog"
import { isVinValid } from "@/lib/vin/vin-validator"
import { AhaReveal } from "./aha-reveal"
import { StepContactFirst } from "./step-contact-first"
import { StepVehicleLite } from "./step-vehicle-lite"
import { StepSituation } from "./step-situation"
import { StepInsurance } from "./step-insurance"
import { StepReach } from "./step-reach"
import { BookingFlow, type BookedInfo } from "./booking-flow"
import { DetailRow, FlowCta, FlowHeading, PhasePane, QuietLink, StickyActions } from "./flow-chrome"
import { FlowFrame } from "./flow-frame"
import { progressFor } from "./flow-progress"
import { useIntakeFlowState, type IntakeFlowVehicle } from "./use-intake-flow-state"
import { useConsumerEstimatePolling } from "./use-consumer-estimate-polling"
import { saveTrackingRef, readTrackingRef } from "./consumer-tracking-store"
import { SURFACE_RULES, trackHref } from "./surfaces"
import type { Entry, ExtrasStep, IntakeDevice, IntakeSurface, Phase } from "./intake-flow-types"
import type { DamageZone } from "./types"
import { CONSUMER_V2 } from "./copy"
import { CaptureFlow, type CaptureView } from "./capture/capture-flow"
import { HandoffScreen } from "./handoff/handoff-screen"
import { CAPTURE_COPY } from "./capture/capture-copy"
import { capturedCount, planFor } from "./capture/shot-plan"
import { usePreviewRegistry } from "./capture/uploads/preview-registry"
import { decideCapability, readEnvironment } from "./capture/camera/capability"
import { prefetchVision } from "./capture/brain/vision-client"
import { useShotUploads } from "./capture/uploads/use-shot-uploads"
import type { ShotId, ShotRecord } from "./capture/types"
import { bodyTypeFor, DEFAULT_BODY } from "./capture/body-type"
import { makeName, type VinActions, type VinHow } from "./capture/vin/use-vin-step"
import { extractVin } from "./capture/vin/vin-text"

// Consumer intake orchestrator — contact-first. The lead exists the moment
// contact submits; the damage-led photo walk attaches each photo as it's
// taken; "Send photos" starts the AI read; the questions run WHILE the
// analysis works underneath; the reveal lands after the last question.
// Surface behaviour (draft persistence, track link, layout) comes from
// SURFACE_RULES, never from branches.

type AnalysisState = "idle" | "reading" | "ready"

// "That's not my car" after a VIN read: everything it filled goes.
const NO_VEHICLE: Partial<IntakeFlowVehicle> = {
  vin: "",
  year: "",
  make: "",
  model: "",
  trim: "",
  type: null,
  plate: "",
  decodedFrom: null,
}

export function ConsumerFlow({
  shop,
  surface,
  device,
  entry,
  continuationToken,
}: {
  shop: PublicShopProfile
  surface: IntakeSurface
  device: IntakeDevice
  entry: Entry
  continuationToken?: string
}) {
  const navigate = useNavigate()
  const rules = SURFACE_RULES[surface]
  const shopWantsVehicle = shop.intake.extras.vehicle
  const { state, actions, resumed, unsentPhotos } = useIntakeFlowState({
    shopId: String(shop.id),
    surface,
    device,
    entry,
    continuationToken,
    shopWantsVehicle,
  })
  const phase = state.phase
  const token = state.result?.trackToken ?? null

  const [creating, setCreating] = useState(false)
  const [contactError, setContactError] = useState<string | null>(null)
  const [laterBusy, setLaterBusy] = useState(false)
  const [sending, setSending] = useState(false)
  const [photoNotice, setPhotoNotice] = useState<string | null>(() =>
    unsentPhotos > 0 ? CAPTURE_COPY.review.lostOnReload : null,
  )
  const [appendNonce, setAppendNonce] = useState(0)
  const [captureView, setCaptureView] = useState<CaptureView["kind"]>("zones")
  const [captureBack, setCaptureBack] = useState<(() => void) | null>(null)
  const [bookingBack, setBookingBack] = useState<(() => void) | null>(null)
  const inFlight = useRef(false)
  // Where an "edit my details" trip to the contact screen returns to.
  const jumpBackRef = useRef<Phase>("photos")

  // Warm the car detector while they type their details (phone, live camera
  // likely): by the time the camera opens it's ready.
  useEffect(() => {
    if (device !== "phone") return
    const capability = decideCapability(readEnvironment())
    if (capability.mode === "smart") prefetchVision()
  }, [device])

  // ── The photo walk. ──
  const plan = useMemo(
    () => planFor(state.capture, state.vehicle.vin, shopWantsVehicle),
    [state.capture, state.vehicle.vin, shopWantsVehicle],
  )
  const registry = usePreviewRegistry()
  const uploads = useShotUploads({
    token,
    plan,
    capture: state.capture,
    actions,
    registry,
  })
  const sentCount = useMemo(
    () =>
      [...(Object.values(state.capture.shots) as ShotRecord[]), ...state.capture.extras].filter(
        (r) => r.uploadedUrl,
      ).length,
    [state.capture],
  )

  const trackingRef = useMemo(
    () => (rules.persistDraft ? readTrackingRef() : null),
    [rules.persistDraft],
  )
  const trackingForShop =
    trackingRef && String(trackingRef.shopId) === String(shop.id) ? trackingRef : null

  const first = state.customer.name.trim().split(/\s+/)[0] || null

  // ── One track helper — every "track your request" entry goes through it. ──
  const trackLink = token ? trackHref(surface, token, publicOrigin()) : null
  const goTrack = (to?: string) => {
    const link = to ? trackHref(surface, to, publicOrigin()) : trackLink
    if (!link) return
    if (link.kind === "navigate") navigate(link.to)
    else window.open(link.href, "_blank", "noopener")
  }

  // ── Analysis stream (owned here so the questions + reveal read the same one).
  // Starts once the final photo send reached the server. ──
  const analysisStarted =
    state.capture.photosDoneAt !== null && state.synced.photos && sentCount > 0
  const { estimate: polled, status: pollStatus } = useConsumerEstimatePolling(token, {
    enabled: analysisStarted,
    reloadNonce: appendNonce,
  })
  const analysis: AnalysisState = !analysisStarted
    ? "idle"
    : pollStatus === "ready"
      ? "ready"
      : "reading"

  // ── Contact → create the lead (or patch it when editing later). ──
  const handleContactSubmit = async (customer: {
    name: string
    phone: string
    email: string
    smsOptIn: boolean
  }) => {
    if (inFlight.current) return
    inFlight.current = true
    setCreating(true)
    setContactError(null)
    actions.setCustomer(customer)
    try {
      if (state.result) {
        await publicConsumerApi.updateDetails(state.result.trackToken, {
          customer: {
            name: customer.name,
            phone: customer.phone,
            email: customer.email || undefined,
          },
        })
        actions.setPhase(jumpBackRef.current)
      } else {
        const utmEntries = [...new URLSearchParams(window.location.search)].filter(([k]) =>
          k.startsWith("utm_"),
        )
        const res = await publicConsumerApi.createIntake(shop.id, {
          customer: {
            name: customer.name,
            phone: customer.phone,
            email: customer.email || undefined,
          },
          surface,
          device,
          consent: {
            text: withShopName(shop.intake.consent.text, shop.name),
            acceptedAt: new Date().toISOString(),
            smsOptIn: customer.smsOptIn,
          },
          referrer: document.referrer || undefined,
          utm: utmEntries.length > 0 ? Object.fromEntries(utmEntries) : undefined,
        })
        actions.setResult({
          estimateId: res.estimateId,
          trackToken: res.trackToken,
          continueUrl: res.continueUrl,
          createdAt: Date.now(),
        })
        if (rules.persistDraft) {
          saveTrackingRef({
            shopId: shop.id,
            estimateId: res.estimateId,
            trackToken: res.trackToken,
            ts: Date.now(),
          })
        }
        actions.setPhase(device === "desktop" ? "handoff" : "photos")
      }
    } catch (err) {
      setContactError(
        err instanceof ApiError ? err.message : CONSUMER_V2.contact.errors.generic,
      )
    } finally {
      inFlight.current = false
      setCreating(false)
    }
  }

  // ── Extras — order from the shop's config; reach is always the finisher. ──
  // The car question isn't asked once the VIN is in (the camera's first
  // step reads it). Once it has been on screen it keeps its place, even as
  // the answer fills the VIN in (the count and Back stay as they were).
  const [carAsked, setCarAsked] = useState(false)
  const carKnown = isVinValid(state.vehicle.vin)
  const askCar = !carKnown || carAsked || (phase === "extras" && state.extrasStep === "vehicle")
  const extrasOrder = useMemo<ExtrasStep[]>(
    () => [
      ...(shop.intake.extras.vehicle && askCar ? (["vehicle"] as const) : []),
      ...(shop.intake.extras.situation ? (["situation"] as const) : []),
      ...(shop.intake.extras.insurance ? (["insurance"] as const) : []),
      "reach",
    ],
    [shop.intake.extras, askCar],
  )
  // The question on screen. A step this shop doesn't ask (a desktop taking
  // over from the phone, a shop that changed its settings since the draft)
  // falls through to the first one it does.
  const extrasStep = extrasOrder.includes(state.extrasStep) ? state.extrasStep : extrasOrder[0]

  const goExtras = () => {
    actions.setExtrasStep(extrasOrder[0])
    actions.setPhase("extras")
  }

  // Fire-and-forget with ONE retry; `synced` tells the reach step's full
  // re-send whether anything was dropped along the way.
  const saveDetails = (patch: IntakeDetailsPatch) => {
    if (!token) return
    actions.markSynced(false)
    void (async () => {
      try {
        await publicConsumerApi.updateDetails(token, patch)
        actions.markSynced(true)
      } catch {
        try {
          await publicConsumerApi.updateDetails(token, patch)
          actions.markSynced(true)
        } catch {
          // Still unsynced — the reach step re-sends the whole blob.
        }
      }
    })()
  }

  // ── Photos ──

  // New damage answer. Photos for shots the new walk no longer has are kept
  // as extras (never silently dropped).
  const handleZonesChosen = (zones: DamageZone[], unsure: boolean) => {
    const nextPlan = planFor(
      { ...state.capture, zones, unsure, zonesConfirmed: true },
      state.vehicle.vin,
      shopWantsVehicle,
    )
    const keep = new Set(nextPlan.map((s) => s.id))
    const orphaned = (Object.keys(state.capture.shots) as ShotId[]).filter((id) => !keep.has(id))
    actions.setZones(zones, unsure)
    for (const id of orphaned) uploads.orphan(id)
    saveDetails({ damageZones: unsure ? [] : zones })
  }

  const finishPhotos = () => {
    // A correction from the reveal goes straight back to it; otherwise the
    // questions run while the analysis works.
    if (state.extrasDoneAt) {
      setAppendNonce((n) => n + 1)
      actions.setPhase("reveal")
    } else {
      goExtras()
    }
  }

  const handleSend = async () => {
    if (sending) return
    setPhotoNotice(null)
    if (sentCount === 0 && uploads.pending === 0) {
      // Nothing to send — continue without photos (the reveal says so).
      finishPhotos()
      return
    }
    setSending(true)
    actions.markPhotosDone()
    const result = await uploads.sendFinal()
    setSending(false)
    if (result === "sent") {
      setAppendNonce((n) => n + 1)
      finishPhotos()
    } else if (result === "offline") {
      toast(CAPTURE_COPY.review.offlineSend)
      finishPhotos()
    } else {
      setPhotoNotice(CAPTURE_COPY.review.sendFailed)
    }
  }

  // A send that couldn't finish (offline, reload) or a photo that landed after
  // "Send photos" is sent again as soon as it can be — once the consumer has
  // moved on from the photo step (on it, their own Send button is in charge).
  const { online, pending, sendFinal } = uploads
  const finalRetry = useRef(false)
  useEffect(() => {
    if (finalRetry.current || sending || phase === "photos") return
    if (state.capture.photosDoneAt === null || state.synced.photos) return
    if (!online || pending > 0 || !token) return
    finalRetry.current = true
    void sendFinal().then((r) => {
      finalRetry.current = false
      if (r === "sent") setAppendNonce((n) => n + 1)
    })
  }, [state.capture.photosDoneAt, state.synced.photos, online, pending, sendFinal, token, sending, phase])

  // The camera's first step: the VIN (barcode, photo of the sticker, or
  // typed), then "Is it a 2018 Nissan Altima?". Yes fills the car (its body
  // type then draws the camera's "this view" picture), and the car question
  // is never asked.
  const confirmVinRef = useRef<VinActions["confirm"]>(() => {})
  useLayoutEffect(() => {
    confirmVinRef.current = (vin, found, how) => {
      const car: Partial<IntakeFlowVehicle> = found
        ? {
            vin: found.vin || vin,
            year: found.year ?? "",
            make: makeName(found.make),
            model: found.model ?? "",
            trim: found.trim ?? "",
            type: found.type ?? null,
            decodedFrom: DECODED_FROM[how],
          }
        : { ...NO_VEHICLE, plate: state.vehicle.plate, vin, decodedFrom: DECODED_FROM[how] }
      actions.setVehicle(car)
      saveDetails({ vehicle: vehiclePatchFrom({ ...state.vehicle, ...car }) })
    }
  })
  const vinActions = useMemo<VinActions>(
    () => ({ ...VIN_LOOKUPS, confirm: (vin, found, how) => confirmVinRef.current(vin, found, how) }),
    [],
  )

  // "Text me a link to add photos later" — the lead already exists, so the
  // link just re-opens this flow on their phone.
  const handleLater = async () => {
    if (!token || laterBusy) return
    setLaterBusy(true)
    try {
      const res = await publicConsumerApi.sendHandoff(token)
      actions.markPhotosLater()
      toast.success(CAPTURE_COPY.brief.laterSent(res.sentTo))
      goExtras()
    } catch {
      toast.error("Couldn't send the link. Try again.")
    } finally {
      setLaterBusy(false)
    }
  }

  // ── Extras ──

  const vehiclePatchFrom = (v: IntakeFlowVehicle): IntakeVehiclePatch | undefined => {
    if (!v.vin && !v.plate && !v.year && !v.make && !v.model) return undefined
    return {
      vin: v.vin || undefined,
      year: v.year || undefined,
      make: v.make || undefined,
      model: v.model || undefined,
      trim: v.trim || undefined,
      type: v.type ?? undefined,
      plate: v.plate || undefined,
      decodedFrom: v.decodedFrom,
    }
  }

  const advanceExtras = (from: ExtrasStep) => {
    const i = extrasOrder.indexOf(from)
    const next = extrasOrder[i + 1]
    if (next) actions.setExtrasStep(next)
    else finishExtras()
  }

  // The reach CTA — re-send the FULL details blob (idempotent) + extrasDoneAt,
  // then hand over to the reveal.
  const finishExtras = () => {
    actions.markExtrasDone()
    saveDetails({
      vehicle: vehiclePatchFrom(state.vehicle),
      situation: state.situation,
      insurance: state.insurance,
      contactPref: {
        channel: state.contactPref.channel ?? "text",
        bestTime: state.contactPref.bestTime,
      },
      extrasDoneAt: new Date().toISOString(),
    })
    actions.setPhase("reveal")
  }

  const handleRevealSeen = () => {
    actions.markRevealSeen()
    if (token) {
      void publicConsumerApi
        .updateDetails(token, { revealSeenAt: new Date().toISOString() })
        .catch(() => {
          /* cosmetic stamp — never block the reveal */
        })
    }
  }

  // ── Back, per screen. ──
  const editContact = useCallback(() => {
    jumpBackRef.current = state.phase === "contact" ? "photos" : state.phase
    actions.setPhase("contact")
  }, [state.phase, actions])

  // Back out of the first photo screen: to the QR handoff on a computer, to
  // the contact details on a phone. Stable, because the frame holds it.
  const backToHandoff = useCallback(() => actions.setPhase("handoff"), [actions])
  const exitPhotosBack = device === "desktop" ? backToHandoff : editContact
  const handleCaptureView = useCallback((view: CaptureView) => setCaptureView(view.kind), [])
  const handleSetBack = useCallback(
    (handler: (() => void) | null) => setCaptureBack(() => handler),
    [],
  )
  const handleSetBookingBack = useCallback(
    (handler: (() => void) | null) => setBookingBack(() => handler),
    [],
  )

  let onBack: (() => void) | null = null
  if (phase === "contact" && state.result) onBack = () => actions.setPhase(jumpBackRef.current)
  else if (phase === "handoff") onBack = editContact
  else if (phase === "photos") onBack = captureBack
  else if (phase === "extras") {
    const i = extrasOrder.indexOf(extrasStep)
    onBack = i > 0 ? () => actions.setExtrasStep(extrasOrder[i - 1]) : () => actions.setPhase("photos")
  } else if (phase === "booking") onBack = bookingBack ?? (() => actions.setPhase("offer"))

  // Hero for analyzing/reveal — prefer a damage close-up, then a closer shot.
  const heroShot = useMemo(() => {
    const ordered = [...plan].sort((a, b) => rank(a.kind) - rank(b.kind))
    for (const shot of ordered) {
      const r = state.capture.shots[shot.id]
      if (r?.uploadedUrl || r?.previewUrl) return r
    }
    return state.capture.extras[0] ?? null
  }, [plan, state.capture])
  const heroPhotoUrl = heroShot ? heroShot.previewUrl || heroShot.uploadedUrl : null
  const heroSideLabel = heroShot
    ? sideLabel(heroShot.plannedSide ?? heroShot.detectedSide) || "Your vehicle"
    : "Your vehicle"

  // ── The reveal is the one full-bleed screen (deep pine, no chrome). ──
  if (phase === "reveal") {
    return (
      <div data-phase="reveal">
        <AhaReveal
          shop={shop}
          first={first}
          phone={state.customer.phone}
          estimate={polled}
          status={pollStatus}
          noPhotos={!analysisStarted}
          heroPhotoUrl={heroPhotoUrl}
          heroSideLabel={heroSideLabel}
          onContinue={() => actions.setPhase("offer")}
          onAddPhoto={() => actions.setPhase("photos")}
          onRevealSeen={handleRevealSeen}
        />
      </div>
    )
  }

  const progress = progressFor({
    phase,
    // No count until the walk exists (it depends on where the damage is).
    photos: state.capture.zonesConfirmed
      ? { done: capturedCount(plan, state.capture), total: plan.length }
      : { done: 0, total: 0 },
    questions: { index: extrasOrder.indexOf(extrasStep), count: extrasOrder.length },
  })

  const focusKey =
    phase === "photos" ? `photos:${captureView}` : phase === "extras" ? `extras:${extrasStep}` : phase

  return (
    <FlowFrame
      shop={shop}
      phase={phase}
      wide={phase === "handoff"}
      progress={progress}
      onBack={onBack}
      focusKey={focusKey}
      status={phase === "extras" ? <AnalysisStatus analysis={analysis} /> : undefined}
    >
      <AnimatePresence mode="wait" initial={false}>
        {phase === "contact" && (
          <StepContactFirst
            key="contact"
            shop={shop}
            device={device}
            customer={state.customer}
            setCustomer={actions.setCustomer}
            resumed={resumed && !state.result}
            hasTrackingRef={!!trackingForShop && !state.result}
            onTrack={() => trackingForShop && goTrack(trackingForShop.trackToken)}
            onStartFresh={actions.reset}
            saving={creating}
            error={contactError}
            onSubmit={(c) => void handleContactSubmit(c)}
          />
        )}

        {phase === "handoff" && token && (
          <HandoffScreen
            key="handoff"
            shop={shop}
            token={token}
            customerPhone={state.customer.phone}
            textsAllowed={state.customer.smsOptIn}
            shopWantsVehicle={shopWantsVehicle}
            onUploadHere={() => actions.setPhase("photos")}
            onContinueHere={(estimate) => actions.hydrateFromEstimate(estimate, token)}
          />
        )}

        {phase === "photos" && (
          <PhasePane key="photos">
            {entry.kind === "continuation" && first && captureView !== "walk" && (
              <p className="mb-2 mt-1 text-[14px] font-semibold text-emerald-700">
                Hi {first}, pick up right where you left off.
              </p>
            )}
            <CaptureFlow
              plan={plan}
              capture={state.capture}
              uploads={uploads}
              onZonesChosen={handleZonesChosen}
              onSkip={actions.skipShot}
              onAccept={(key) => actions.patchShot(key, { accepted: true })}
              onSend={() => void handleSend()}
              sending={sending}
              notice={photoNotice}
              onLater={
                shop.intake.extras.uploadLater && !state.photosLaterSent && token
                  ? () => void handleLater()
                  : null
              }
              laterBusy={laterBusy}
              onExitBack={exitPhotosBack}
              forceNative={device === "desktop"}
              setBack={handleSetBack}
              onViewChange={handleCaptureView}
              vin={vinActions}
              bodyType={bodyTypeFor(state.vehicle.type) ?? DEFAULT_BODY}
              onVoiceChange={actions.setVoice}
            />
          </PhasePane>
        )}

        {phase === "extras" && extrasStep === "vehicle" && (
          <StepVehicleLite
            key="extras-vehicle"
            device={device}
            vehicle={state.vehicle}
            setVehicle={actions.setVehicle}
            required={shop.intake.required.vehicle}
            onDone={(vehicle) => {
              setCarAsked(true)
              saveDetails({ vehicle: vehiclePatchFrom(vehicle) })
              advanceExtras("vehicle")
            }}
            onSkip={() => {
              setCarAsked(true)
              advanceExtras("vehicle")
            }}
            onRejectFound={() => {
              actions.setVehicle(NO_VEHICLE)
              saveDetails({ vehicle: NO_VEHICLE })
            }}
          />
        )}

        {phase === "extras" && extrasStep === "situation" && (
          <StepSituation
            key="extras-situation"
            situation={state.situation}
            setSituation={actions.setSituation}
            onDone={() => {
              saveDetails({ situation: state.situation })
              advanceExtras("situation")
            }}
            onSkip={() => advanceExtras("situation")}
          />
        )}

        {phase === "extras" && extrasStep === "insurance" && (
          <StepInsurance
            key="extras-insurance"
            insurance={state.insurance}
            setInsurance={actions.setInsurance}
            onDone={() => {
              saveDetails({ insurance: state.insurance })
              advanceExtras("insurance")
            }}
            onSkip={() => advanceExtras("insurance")}
          />
        )}

        {phase === "extras" && extrasStep === "reach" && (
          <StepReach
            key="extras-reach"
            shopName={shop.name.split(" ")[0]}
            contactPref={state.contactPref}
            setContactPref={actions.setContactPref}
            onDone={finishExtras}
          />
        )}

        {phase === "offer" && state.result && (
          <OfferScene
            key="offer"
            shop={shop}
            first={first}
            phone={state.customer.phone}
            showBooking={shop.intake.extras.booking}
            onBook={() => actions.setPhase("booking")}
            onSkip={() => actions.setPhase("done")}
          />
        )}

        {phase === "booking" && state.result && (
          <BookingFlow
            key="booking"
            shop={shop}
            trackToken={state.result.trackToken}
            onBooked={(info) => {
              actions.setBooked(info)
              actions.setPhase("done")
            }}
            onCancel={() => actions.setPhase("offer")}
            setBack={handleSetBookingBack}
          />
        )}

        {phase === "done" && (
          <DoneScene
            key="done"
            shop={shop}
            booked={state.booked}
            onTrack={trackLink ? () => goTrack() : null}
          />
        )}
      </AnimatePresence>
    </FlowFrame>
  )
}

// Where the camera's VIN came from, as the lead records it.
const DECODED_FROM: Record<VinHow, IntakeFlowVehicle["decodedFrom"]> = {
  scan: "vin-scan",
  photo: "vin-photo",
  typed: "manual",
}

// Reading a sticker photo (the server) and looking a VIN up (NHTSA through
// the server). A miss of either is null, never an error on screen.
const VIN_LOOKUPS: Omit<VinActions, "confirm"> = {
  async fromPhoto(file: File): Promise<string | null> {
    try {
      const { vin } = await publicConsumerApi.vinFromImage(file)
      return extractVin(vin)
    } catch {
      return null
    }
  },
  async lookup(vin: string): Promise<DecodedVehicle | null> {
    try {
      return await publicConsumerApi.decodeVin(vin)
    } catch {
      return null
    }
  },
}

function rank(kind: string): number {
  return kind === "closeup" ? 0 : kind === "closer" ? 1 : kind === "wide" ? 2 : 3
}

/* ── Analysis status — beside the progress label while the questions run. ── */
function AnalysisStatus({ analysis }: { analysis: AnalysisState }) {
  if (analysis === "idle") return null
  return analysis === "reading" ? (
    <span role="status" className="inline-flex shrink-0 items-center gap-1.5 text-[13px] font-semibold text-zinc-600">
      <span className="size-3.5 animate-spin rounded-full border-2 border-ink/15 border-t-signal" aria-hidden />
      {CONSUMER_V2.extras.readingPill}
    </span>
  ) : (
    <span role="status" className="inline-flex shrink-0 items-center gap-1 text-[13px] font-semibold text-emerald-700">
      <Check className="size-4 text-signal" strokeWidth={3} aria-hidden />
      {CONSUMER_V2.extras.readDonePill}
    </span>
  )
}

/* ── Sent: the small green mark that says "it's with the shop". ── */
function SentMark({ label }: { label: string }) {
  const reduce = useReducedMotion()
  return (
    <p className="flex items-center gap-2.5 text-[15px] font-semibold text-emerald-700">
      <motion.span
        initial={reduce ? false : { scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 420, damping: 15 }}
        className="grid size-8 place-items-center rounded-full bg-signal text-white"
      >
        <Check className="size-4" strokeWidth={3} aria-hidden />
      </motion.span>
      {label}
    </p>
  )
}

/* ── Offer: a soft booking invite, never a gate. ── */
function OfferScene({
  shop,
  first,
  phone,
  showBooking,
  onBook,
  onSkip,
}: {
  shop: PublicShopProfile
  first: string | null
  phone: string
  showBooking: boolean
  onBook: () => void
  onSkip: () => void
}) {
  const O = CONSUMER_V2.offer
  const shopFirst = shop.name.split(" ")[0]
  return (
    <PhasePane className="pt-6">
      <SentMark label={O.sent(shopFirst)} />
      <FlowHeading className="mt-5 text-[30px] font-bold leading-[1.12] tracking-tight text-ink">
        {O.title(first)}
      </FlowHeading>
      <p className="mt-3 text-[16px] leading-relaxed text-zinc-600">{O.body(shopFirst, phone || "you")}</p>

      {showBooking && (
        <section className="mt-8 border-t border-ink/10 pt-6">
          <h2 className="text-[20px] font-semibold leading-snug text-ink">{O.inviteTitle}</h2>
          <p className="mt-1.5 text-[15px] leading-relaxed text-zinc-600">{O.inviteBody}</p>
        </section>
      )}

      <StickyActions className="space-y-1">
        {showBooking ? (
          <>
            <FlowCta onClick={onBook}>
              <CalendarCheck01 className="size-5" aria-hidden />
              {O.book}
            </FlowCta>
            <QuietLink onClick={onSkip}>{O.skip}</QuietLink>
          </>
        ) : (
          <FlowCta onClick={onSkip}>{O.done}</FlowCta>
        )}
      </StickyActions>
    </PhasePane>
  )
}

/* ── Done: never claims an email went out; track via the one helper. ── */
function DoneScene({
  shop,
  booked,
  onTrack,
}: {
  shop: PublicShopProfile
  booked: BookedInfo | null
  onTrack: (() => void) | null
}) {
  const D = CONSUMER_V2.done
  return (
    <PhasePane className="pt-6">
      <SentMark label={booked ? "Booked" : `Sent to ${shop.name.split(" ")[0]}`} />
      <FlowHeading className="mt-5 text-[30px] font-bold leading-[1.12] tracking-tight text-ink">
        {booked ? D.titleBooked : D.title}
      </FlowHeading>

      {booked && (
        <dl className="mt-6 divide-y divide-ink/10 border-y border-ink/10">
          <DetailRow
            icon={<CalendarCheck01 className="size-5" aria-hidden />}
            label="When"
            title={booked.date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            body={`${formatClock(booked.time)} · ${booked.type === "inspection" ? "Free inspection" : "Drop-off"}`}
          />
          <DetailRow
            icon={<MarkerPin01 className="size-5" aria-hidden />}
            label="Where"
            title={shop.name}
            body={[shop.street, shop.city, shop.state].filter(Boolean).join(", ") || undefined}
          />
        </dl>
      )}

      <p className="mt-6 flex items-start gap-2.5 text-[15px] leading-relaxed text-zinc-600">
        <MessageChatCircle className="mt-0.5 size-5 shrink-0 text-zinc-500" aria-hidden />
        {D.trackLine}
      </p>

      {onTrack && (
        <StickyActions>
          <FlowCta onClick={onTrack}>
            {D.trackCta}
            <ArrowRight className="size-4" aria-hidden />
          </FlowCta>
        </StickyActions>
      )}
    </PhasePane>
  )
}

function formatClock(time: string): string {
  const [h, m] = time.split(":").map(Number)
  const period = h >= 12 ? "PM" : "AM"
  const hour = h % 12 || 12
  return `${hour}:${String(m).padStart(2, "0")} ${period}`
}
